/**
 * ColumnChart: hand-built SVG column chart (one or two overlaid series) with hover, keyboard access, a
 * hover readout and legend toggles.
 *
 * Port of LAE `features/productivity/charts/ColumnChart.jsx`. Drawn at the measured pixel width (a
 * ResizeObserver on the plot, no viewBox stretch) so 12px text stays crisp. Every colour is an EMERGE token
 * read as a CSS variable (`var(--chart-1)`, `var(--hairline)`, ...), never a raw value.
 *
 * Deliberate differences from LAE (spec section 4 and 0.4):
 * - Default fills are `var(--chart-1)` / `var(--chart-2)` (LAE: its brand scale). Gridlines use
 *   `var(--hairline)`; the off legend swatch is `var(--hairline)`; muted text is `var(--fg-subtle)`.
 * - The legend's "Click to hide ..." hover help is dropped (no helper text). The buttons keep
 *   `aria-pressed` and their label only.
 * - The Rate row is opt-in through `showRate` (LAE always showed it with two series, which is meaningless
 *   for whatsapps vs emails). The trend card passes it for the calls metric only.
 * - The hover readout is not portalled (LAE does not portal it either). It stays inside the chart box and
 *   is clamped horizontally as well as vertically, so it never widens the page at 320px.
 * - null / undefined values draw as 0-height bars (the spec's zero-fill for series); the readout shows
 *   whatever `formatValue` makes of 0, exactly as LAE did.
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent, MouseEvent, RefObject } from 'react';
import { cx } from '@/lib/cx';

export type ColumnChartValue = number | null | undefined;

export interface ColumnChartSeries {
  /** Stable id: the legend's hidden set stores it. */
  key: string;
  label: string;
  /** One value per bucket, aligned with `buckets`. */
  values: readonly ColumnChartValue[];
  /** A CSS colour, normally `var(--token)`. Defaults to the series' slot in `SERIES_FILL`. */
  fill?: string;
}

export interface ColumnChartTooltipRow {
  label: string;
  value: string;
  fill?: string;
}

export interface ColumnChartProps {
  buckets: readonly string[];
  series: readonly ColumnChartSeries[];
  height?: number;
  formatX?: (bucket: string) => string;
  formatTooltipX?: (bucket: string) => string;
  formatValue?: (value: number) => string;
  /** A bucket to keep shaded (e.g. the selected day). */
  highlight?: string | null;
  onBucketClick?: (bucket: string) => void;
  /** Extra readout rows under the series values for the bucket at `index`. */
  tooltipRows?: (index: number) => readonly (ColumnChartTooltipRow | null | false | undefined)[] | null | undefined;
  /** Fixed label indexes (still thinned when they collide). */
  xTicks?: readonly number[];
  /** Controlled legend: the series keys switched off. Only used together with `onHiddenChange`. */
  hidden?: readonly string[];
  onHiddenChange?: (hidden: string[]) => void;
  /** Show series 1 / series 0 as a Rate row in the readout (two series only). */
  showRate?: boolean;
  ariaLabel: string;
  className?: string;
}

const SERIES_FILL = ['var(--chart-1)', 'var(--chart-2)'] as const;
const AXIS_FONT = 12;
const VALUE_FONT = 11;
const FONT_STACK = "Inter, ui-sans-serif, system-ui, sans-serif";
const LABEL_GAP = 10;
const PAD_B = 26;
const MIN_PAD_R = 8;

let measureCtx: CanvasRenderingContext2D | null | undefined;
/** Canvas text width; falls back to an average glyph estimate where canvas is unavailable. */
function textWidth(str: string, size: number = AXIS_FONT): number {
  if (measureCtx === undefined) {
    try {
      measureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return str.length * size * 0.58;
  measureCtx.font = `${size}px ${FONT_STACK}`;
  return measureCtx.measureText(str).width;
}

/** 1.2k / 12k / 1.2M; small numbers keep at most one decimal. */
function compact(v: number): string {
  const n = Number.isFinite(v) ? v : 0;
  const a = Math.abs(n);
  const trim = (x: number) => String(Number(x.toFixed(1)));
  if (a >= 1e6) return `${trim(n / 1e6)}M`;
  if (a >= 1e3) return `${trim(n / 1e3)}k`;
  return trim(n);
}

/** Nice step (1, 2, 2.5, 5 x 10^n; 1, 2, 5, 10 below 10; never below 1) giving 4-5 ticks from 0. */
function niceScale(dataMax: number): { max: number; ticks: number[] } {
  if (!(dataMax > 0)) return { max: 4, ticks: [0, 1, 2, 3, 4] };
  const raw = dataMax / 4;
  const exp = 10 ** Math.floor(Math.log10(raw));
  const f = raw / exp;
  const steps = exp >= 10 ? [1, 2, 2.5, 5, 10] : [1, 2, 5, 10];
  const step = Math.max(1, (steps.find((s) => f <= s) ?? 10) * exp);
  const count = Math.max(1, Math.ceil(dataMax / step));
  const ticks = Array.from({ length: count + 1 }, (_, i) => i * step);
  return { max: count * step, ticks };
}

/** Indexes to label: every bar when they fit, else a regular stride that always keeps first and last. */
function pickTicks(n: number, slot: number, widths: readonly number[], xTicks?: readonly number[]): number[] {
  if (!n || !(slot > 0)) return [];
  const maxW = Math.max(0, ...widths);
  const need = (maxW + LABEL_GAP) / slot; // slots one label occupies
  if (xTicks) {
    const base = [...new Set(xTicks.filter((i) => i >= 0 && i < n))].sort((a, b) => a - b);
    if (base.length < 2) return base;
    let spacing = Infinity;
    for (let j = 1; j < base.length; j += 1) spacing = Math.min(spacing, (base[j] ?? 0) - (base[j - 1] ?? 0));
    const k = Math.max(1, Math.ceil(need / spacing));
    return base.filter((_, j) => j % k === 0);
  }
  if (need <= 1) return Array.from({ length: n }, (_, i) => i);
  const stride = Math.ceil(need);
  const out = [0];
  for (let i = stride; i < n - 1; i += stride) if (n - 1 - i >= stride) out.push(i);
  if (n > 1 && n - 1 - (out[out.length - 1] ?? 0) >= need) out.push(n - 1);
  return out;
}

function useWidth(ref: RefObject<HTMLDivElement | null>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

const num = (v: ColumnChartValue): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const identity = (b: string) => b;

interface ShownSeries extends ColumnChartSeries {
  fill: string;
  idx: number;
}

export function ColumnChart({
  buckets,
  series,
  height = 220,
  formatX = identity,
  formatTooltipX,
  formatValue,
  highlight,
  onBucketClick,
  tooltipRows,
  xTicks,
  hidden: hiddenProp,
  onHiddenChange,
  showRate = false,
  ariaLabel,
  className,
}: ColumnChartProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const width = useWidth(wrapRef);
  const [active, setActive] = useState<number | null>(null);
  const [ownHidden, setOwnHidden] = useState<Set<string>>(() => new Set());
  const controlled = typeof onHiddenChange === 'function';
  const hiddenSig = controlled && hiddenProp ? JSON.stringify(hiddenProp) : '[]';
  const hidden = useMemo<Set<string>>(
    () => (controlled ? new Set<string>(JSON.parse(hiddenSig) as string[]) : ownHidden),
    [controlled, hiddenSig, ownHidden],
  );

  const fmt = formatValue ?? compact;
  const tipX = formatTooltipX ?? formatX;
  const n = buckets.length;

  const shown = useMemo<ShownSeries[]>(
    () => series.slice(0, 2).map((s, k) => ({ ...s, fill: s.fill ?? SERIES_FILL[k] ?? SERIES_FILL[0], idx: k })),
    [series],
  );
  let visible = shown.filter((s) => !hidden.has(s.key));
  if (!visible.length) visible = shown;
  const primary = visible[0];

  const allZero = shown.every((s) => s.values.every((v) => num(v) === 0));
  const dataMax = Math.max(0, ...visible.flatMap((s) => s.values.map(num)));
  const { max: yMax, ticks: yTicks } = niceScale(dataMax);

  const xLabels = useMemo(() => buckets.map((b) => String(formatX(b) ?? '')), [buckets, formatX]);
  const xWidths = useMemo(() => xLabels.map((l) => textWidth(l)), [xLabels]);
  const yLabels = yTicks.map((t) => String(fmt(t)));
  const gutter = Math.max(24, Math.ceil(Math.max(0, ...yLabels.map((l) => textWidth(l)))) + 10);

  // First pass with minimum padding, then widen the edges so the first/last labels never clip.
  const slot0 = n ? Math.max(0, width - gutter - MIN_PAD_R) / n : 0;
  const padL = Math.max(gutter, Math.ceil((xWidths[0] ?? 0) / 2 - slot0 / 2) + 2);
  const padR = Math.max(MIN_PAD_R, Math.ceil((xWidths[n - 1] ?? 0) / 2 - slot0 / 2) + 2);
  const plotW = Math.max(0, width - padL - padR);
  const slot = n ? plotW / n : 0;
  const showValues = slot >= 28 && !allZero;
  const padT = showValues ? 20 : 10;
  const plotH = Math.max(0, height - padT - PAD_B);
  const baseY = padT + plotH;

  const barW = Math.max(2, Math.min(slot * 0.66, 44));
  const innerW = Math.max(1, barW * 0.6);
  const rx = barW >= 6 ? 3 : 0;
  const yOf = (v: number) => baseY - (v / yMax) * plotH;
  const centreOf = (i: number) => padL + slot * i + slot / 2;
  const tickIdx = pickTicks(n, slot, xWidths, xTicks);

  const activeIdx = active != null && active < n ? active : null;
  const highlightIdx = highlight != null ? buckets.indexOf(highlight) : -1;

  const toggleIn = (prev: ReadonlySet<string>, key: string): Set<string> => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else if (shown.filter((s) => !next.has(s.key)).length > 1) next.add(key);
    return next;
  };
  const toggle = (key: string) => {
    if (onHiddenChange) onHiddenChange([...toggleIn(hidden, key)]);
    else setOwnHidden((prev) => toggleIn(prev, key));
  };

  const indexAt = (e: PointerEvent<SVGRectElement> | MouseEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.floor((e.clientX - rect.left) / (slot || 1));
    return Math.min(n - 1, Math.max(0, i));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!n) return;
    const start = activeIdx ?? (highlightIdx >= 0 ? highlightIdx : null);
    let next: number;
    if (e.key === 'ArrowRight') next = start == null ? 0 : Math.min(n - 1, start + 1);
    else if (e.key === 'ArrowLeft') next = start == null ? n - 1 : Math.max(0, start - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    else if (e.key === 'Escape') {
      if (activeIdx != null) {
        e.preventDefault();
        e.stopPropagation();
        setActive(null);
      }
      return;
    } else if ((e.key === 'Enter' || e.key === ' ') && onBucketClick && activeIdx != null) {
      e.preventDefault();
      const bucket = buckets[activeIdx];
      if (bucket !== undefined) onBucketClick(bucket);
      return;
    } else return;
    e.preventDefault();
    setActive(next);
  };

  const rateOf = (i: number): string | null => {
    if (!showRate || shown.length < 2) return null;
    const a = num(shown[0]?.values[i]);
    if (!a) return '–';
    return `${Math.round((num(shown[1]?.values[i]) / a) * 100)}%`;
  };

  const extraRows: ColumnChartTooltipRow[] =
    activeIdx != null && tooltipRows
      ? (tooltipRows(activeIdx) ?? []).filter((r): r is ColumnChartTooltipRow => Boolean(r))
      : [];

  const activeBucket = activeIdx != null ? buckets[activeIdx] : undefined;
  const activeRate = activeIdx != null ? rateOf(activeIdx) : null;
  const liveText =
    activeIdx == null || activeBucket === undefined
      ? ''
      : `${tipX(activeBucket)}: ${visible.map((s) => `${s.label} ${fmt(num(s.values[activeIdx]))}`).join(', ')}${
          activeRate ? `, rate ${activeRate}` : ''
        }${extraRows.map((r) => `, ${r.label} ${r.value}`).join('')}`;

  // Place the readout beside the active bar: to its left past the midpoint, else to its right. It is
  // lifted so it never runs past the bottom, and clamped inside the chart so it never widens the page.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el || activeIdx == null) return;
    const w = el.offsetWidth;
    const c = padL + slot * activeIdx + slot / 2;
    const onLeft = c > width / 2;
    const desired = onLeft ? c - slot / 2 - 6 - w : c + slot / 2 + 6;
    el.style.left = `${Math.max(0, Math.min(desired, width - w))}px`;
    el.style.top = `${Math.max(0, Math.min(padT, height - el.offsetHeight))}px`;
  });

  const barCls = '[transition:y_150ms_ease-out,height_150ms_ease-out] motion-reduce:[transition:none]';
  const textStyle = { fontFamily: 'var(--font-sans)', fontVariantNumeric: 'tabular-nums' } as const;

  return (
    <div className={cx('relative w-full min-w-0', className)}>
      {shown.length >= 2 && (
        <div className="mb-2 flex flex-wrap justify-end gap-1.5">
          {shown.map((s) => {
            const on = visible.includes(s);
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(s.key)}
                className={cx(
                  'inline-flex items-center gap-1.5 rounded-md border border-hairline px-2 py-0.5 text-xs transition-colors duration-150 motion-reduce:transition-none max-sm:py-1.5',
                  on ? 'bg-surface text-fg' : 'bg-surface-sunken text-fg-subtle line-through',
                )}
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]"
                  style={{ background: on ? s.fill : 'var(--hairline)' }}
                />
                {s.label}
              </button>
            );
          })}
        </div>
      )}
      <div
        ref={wrapRef}
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onBlur={() => setActive(null)}
        className="relative w-full min-w-0 rounded-md"
        style={{ height }}
      >
        {width > 0 && (
          <svg width={width} height={height} aria-hidden="true" className="block select-none">
            {highlightIdx >= 0 && (
              <rect x={padL + slot * highlightIdx} y={padT} width={slot} height={plotH} fill="var(--surface-sunken)" />
            )}
            {activeIdx != null && activeIdx !== highlightIdx && (
              <rect
                x={padL + slot * activeIdx}
                y={padT}
                width={slot}
                height={plotH}
                fill="var(--surface-sunken)"
                opacity={0.75}
              />
            )}

            {yTicks.map((t, j) => {
              const gy = Math.round(yOf(t)) + 0.5;
              return (
                <g key={t}>
                  <line x1={padL} x2={width - padR} y1={gy} y2={gy} stroke="var(--hairline)" strokeWidth="1" />
                  <text
                    x={padL - 8}
                    y={gy}
                    dy="0.35em"
                    fontSize={AXIS_FONT}
                    fill="var(--fg-muted)"
                    textAnchor="end"
                    style={textStyle}
                  >
                    {yLabels[j]}
                  </text>
                </g>
              );
            })}

            {visible.map((s) => {
              const w = visible.length > 1 && s.idx === 1 ? innerW : barW;
              return buckets.map((_, i) => {
                const v = num(s.values[i]);
                const top = v > 0 ? Math.min(yOf(v), baseY - 1) : baseY;
                const h = Math.max(0, baseY - top);
                return (
                  <rect
                    key={`${s.key}-${i}`}
                    x={centreOf(i) - w / 2}
                    y={top}
                    width={w}
                    height={h}
                    rx={Math.min(rx, w / 2, h / 2)}
                    fill={s.fill}
                    className={barCls}
                    style={{ y: top, height: h }}
                  />
                );
              });
            })}

            {showValues &&
              primary &&
              buckets.map((_, i) => {
                const v = num(primary.values[i]);
                if (!v) return null;
                const label = String(fmt(v));
                if (textWidth(label, VALUE_FONT) > slot - 2) return null;
                return (
                  <text
                    key={`v-${i}`}
                    x={centreOf(i)}
                    y={yOf(v) - 5}
                    fontSize={VALUE_FONT}
                    fill="var(--fg)"
                    textAnchor="middle"
                    style={textStyle}
                  >
                    {label}
                  </text>
                );
              })}

            {tickIdx.map((i) => {
              const w = xWidths[i] ?? 0;
              const x = Math.min(Math.max(centreOf(i), w / 2 + 1), width - w / 2 - 1);
              return (
                <text
                  key={`x-${i}`}
                  x={x}
                  y={baseY + 18}
                  fontSize={AXIS_FONT}
                  fill={i === activeIdx ? 'var(--fg)' : 'var(--fg-muted)'}
                  fontWeight={i === activeIdx ? 600 : 400}
                  textAnchor="middle"
                  style={textStyle}
                >
                  {xLabels[i]}
                </text>
              );
            })}

            {allZero && (
              <text
                x={padL + plotW / 2}
                y={padT + plotH / 2}
                dy="0.35em"
                fontSize={AXIS_FONT}
                fill="var(--fg-subtle)"
                textAnchor="middle"
                style={{ fontFamily: 'var(--font-sans)' }}
              >
                No activity
              </text>
            )}

            {n > 0 && (
              <rect
                x={padL}
                y={0}
                width={plotW}
                height={height}
                fill="transparent"
                style={onBucketClick ? { cursor: 'pointer' } : undefined}
                onPointerMove={(e) => setActive(indexAt(e))}
                onPointerDown={(e) => setActive(indexAt(e))}
                onPointerLeave={(e) => {
                  if (e.pointerType === 'mouse') setActive(null);
                }}
                onClick={
                  onBucketClick
                    ? (e) => {
                        const bucket = buckets[indexAt(e)];
                        if (bucket !== undefined) onBucketClick(bucket);
                      }
                    : undefined
                }
              />
            )}
          </svg>
        )}

        {activeIdx != null && activeBucket !== undefined && (
          <div
            ref={tipRef}
            className="pointer-events-none absolute left-0 top-0 z-20 min-w-[120px] max-w-full rounded-md border border-border bg-surface px-2.5 py-2 text-xs shadow-sm"
          >
            <div className="tabular mb-1 font-medium text-fg">{tipX(activeBucket)}</div>
            {visible.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5 leading-5">
                <span aria-hidden="true" className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: s.fill }} />
                <span className="text-fg-muted">{s.label}</span>
                <span className="tabular ml-auto pl-4 text-fg">{fmt(num(s.values[activeIdx]))}</span>
              </div>
            ))}
            {activeRate && (
              <div className="mt-1 flex items-center gap-1.5 border-t border-hairline pt-1 leading-5">
                <span className="text-fg-muted">Rate</span>
                <span className="tabular ml-auto pl-4 text-fg">{activeRate}</span>
              </div>
            )}
            {extraRows.length > 0 && (
              <div className="mt-1 border-t border-hairline pt-1">
                {extraRows.map((r) => (
                  <div key={r.label} className="flex items-center gap-1.5 leading-5">
                    {r.fill && (
                      <span aria-hidden="true" className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: r.fill }} />
                    )}
                    <span className="text-fg-muted">{r.label}</span>
                    <span className="tabular ml-auto pl-4 text-fg">{r.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="sr-only" aria-live="polite">
        {liveText}
      </div>
    </div>
  );
}

export default ColumnChart;
